import { Fragment } from 'react';
import {
  Dialog,
  Transition,
  Disclosure,
  TransitionChild,
  DialogPanel,
  DisclosurePanel,
  DisclosureButton,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  Default,
  Switch,
  Case,
  When,
} from 'react-if';
import { useNavigate } from 'react-router';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { SavedFilterType } from 'shared';

import { classNames }  from '../../../common/lib/classNames';
import { useNavigationOpts } from '../../hooks/useNavigationOpts';
import { WrappedLink } from '../WrappedLink';
import { useHasValidSubscription } from '../../hooks/useHasValidSubscription';
import { Badge } from '../../../common/Atoms/Badge';
import { useGetSubscriptionMetadata } from '../../hooks/useGetSubscription';
import { UpgradeBadge } from '../Molecules/UpgradeBadge';
import { Divider } from '../../../common/Atoms/Divider';
import { useCreateGroupMutation } from '../../services/api/groupApi/group';
import { useGroups } from '../../hooks/useGroups';
import { useModals } from '../../hooks/useModals';
import { mixpanelService } from '../../../common/lib/mixpanel';
import {
  contactAgeRanges,
  actionQuickFilters,
  escalationQuickFilters,
  useQuickFilterNavigation,
} from '../../hooks/useQuickFilterNavigation';
import { invoiceAgingBucketMenuLabel, invoiceAgingBuckets } from '../../lib/invoiceAgingBuckets';

import { SavedFiltersNav } from './SavedFiltersNav';
import { Logo } from './Logo';
import { ContactQuickFiltersNav } from './ContactQuickFiltersNav';

export function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  currentOrg,
}) {
  const { navigation } = useNavigationOpts(!!currentOrg);
  const navigate = useNavigate();
  const hasValidSubscription = useHasValidSubscription();
  const { subscriptionMetadata } = useGetSubscriptionMetadata();
  const [createGroup] = useCreateGroupMutation();
  const { refetch } = useGroups();
  const { show } = useModals();
  // Close mobile sidebar after quick filter navigation
  const {
    navigateToInvoiceAgingBucket,
    navigateWithAgeFilter,
    navigateToEscalationQuickFilter,
    navigateToActionQuickFilter,
  } = useQuickFilterNavigation(() => setSidebarOpen(false));

  const noSub = item => {
    return (!hasValidSubscription && item.requiresSubscription);
  };

  const upgrade = item => {
    if (hasValidSubscription) {
      return item.requiresPaidFeature && !subscriptionMetadata?.features?.[item.requiresPaidFeature];
    }
  };

  const notAllowed = item => {
    return noSub(item) || upgrade(item);
  };

  async function onNewGroup() {
    const res = await createGroup({ organisationId: currentOrg.id }).unwrap();

    const createdId = res?.id;

    if (refetch) {
      // Force ensure the group is in the cache
      await refetch();
    }

    if (createdId) {
      navigate(`${currentOrg.id}/groups/${createdId}`);
    }
  }

  function onBookDemo() {
    show(`call`);
    mixpanelService.demoCallClicked();
  }

  return (
    <Transition show={ sidebarOpen }
      as={ Fragment }>
      <Dialog as={ `div` }
        className={ `relative z-40 md:hidden` }
        onClose={ setSidebarOpen }>
        <TransitionChild
          as={ Fragment }
          enter={ `transition-opacity ease-linear duration-300` }
          enterFrom={ `opacity-0` }
          enterTo={ `opacity-100` }
          leave={ `transition-opacity ease-linear duration-300` }
          leaveFrom={ `opacity-100` }
          leaveTo={ `opacity-0` }
        >
          <div className={ `fixed inset-0 bg-gray-600/75 dark:bg-black/60` } />
        </TransitionChild>

        <div className={ `fixed inset-0 z-40 flex` }>
          <TransitionChild
            as={ Fragment }
            enter={ `transition ease-in-out duration-300 transform` }
            enterFrom={ `-translate-x-full` }
            enterTo={ `translate-x-0` }
            leave={ `transition ease-in-out duration-300 transform` }
            leaveFrom={ `translate-x-0` }
            leaveTo={ `-translate-x-full` }
          >
            <DialogPanel className={ `relative flex w-full max-w-xs flex-1 flex-col bg-paidniceGreen-700 pt-5 pb-4` }>
              <TransitionChild
                as={ Fragment }
                enter={ `ease-in-out duration-300` }
                enterFrom={ `opacity-0` }
                enterTo={ `opacity-100` }
                leave={ `ease-in-out duration-300` }
                leaveFrom={ `opacity-100` }
                leaveTo={ `opacity-0` }
              >
                <div className={ `absolute top-0 right-0 -mr-12 pt-2` }>
                  <button
                    type={ `button` }
                    className={ `ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white` }
                    onClick={ () => setSidebarOpen(false) }
                  >
                    <span className={ `sr-only` }>{ `Close sidebar` }</span>
                    <XMarkIcon className={ `h-6 w-6 text-white` }
                      aria-hidden={ `true` } />
                  </button>
                </div>
              </TransitionChild>
              <div className={ `flex shrink-0 items-center px-4` }>
                <Logo type={ `white` }/>
              </div>
              <div className={ `mt-5 h-0 flex-1 overflow-y-auto` }>
                <nav className={ `space-y-8 px-2` }>
                  { navigation.map((item, i) => {
                    const LinkComponent = item.absolute ? Link : WrappedLink;

                    return (
                      <Switch key={ i }>
                        <Case condition={ item.href === `ADD_NEW_GROUP` }>
                          <a
                            key={ item.name }
                            href={ `#` }
                            className={ classNames(
                              item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                              `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                            ) }
                            onClick={ e => {
                              e.preventDefault();
                              onNewGroup();
                            } }
                          >
                            <>
                              <item.icon
                                className={ classNames(
                                  item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                  `mr-3 shrink-0 h-6 w-6`,
                                ) }
                                aria-hidden={ `true` }
                              />
                              { item.name }
                            </>
                          </a>
                        </Case>
                        <Case condition={ item.href === `BOOK_DEMO` }>
                          <a
                            key={ item.name }
                            href={ `#` }
                            className={ classNames(
                              item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                              `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                            ) }
                            onClick={ e => {
                              e.preventDefault();
                              onBookDemo();
                            } }
                          >
                            <>
                              <item.icon
                                className={ classNames(
                                  item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                  `mr-3 shrink-0 h-6 w-6`,
                                ) }
                                aria-hidden={ `true` }
                              />
                              { item.name }
                            </>
                          </a>
                        </Case>
                        <Case condition={ item.href === `SPACE` }>
                          <div className={ `` }>
                            <Divider
                              weight={ `light` }
                              text={ item.name }
                              spacing={ `small` }
                            />
                          </div>
                        </Case>
                        <Case condition={ item.external }>
                          <a
                            href={ item.href }
                            target={ `_blank` }
                            rel={ `noreferrer` }
                            className={ classNames(
                              item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                              `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                            ) }>
                            <item.icon
                              className={ classNames(
                                item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                `mr-3 shrink-0 h-6 w-6`,
                              ) }
                              aria-hidden={ `true` }
                            />
                            {
                              item.name
                            }
                          </a>
                        </Case>
                        <Case condition={ !item.children }>
                          <LinkComponent
                            key={ item.name }
                            to={ item.href }
                            className={ classNames(
                              item.current ? `bg-paidniceGreen-800 text-white` : `text-paidniceGreen-100 hover:bg-paidniceGreen-600`,
                              `group flex items-center px-2 py-2 text-base font-medium rounded-md`,
                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                            ) }
                            disabled={ notAllowed(item) }
                          >
                            <>
                              <item.icon className={ `mr-4 h-6 w-6 shrink-0 text-paidniceGreen-300` }
                                aria-hidden={ `true` } />
                              <Tooltip title={ item.name }>
                                <span className={ `flex-1 truncate` }>{ item.name }</span>
                              </Tooltip>
                              <When condition={ noSub(item) }>
                                <Badge color={ `gray` }
                                  message={ `No Plan` }
                                  size={ `xs` } />
                              </When>

                              <When condition={ upgrade(item) }>
                                <UpgradeBadge />
                              </When>

                              <When condition={ item.type === `groupOptionDefault` }>
                                <div className={ `ml-0.5` }>
                                  <Badge
                                    color={ `blue` }
                                    message={ `Default` }
                                    size={ `xs` }

                                  />
                                </div>
                              </When>
                            </>
                          </LinkComponent>

                          { /* Quick Filters for Contacts */ }
                          <When condition={ item.name === `Contacts` && item.current }>
                            <div className={ `ml-10 mt-2 space-y-1` }>
                              <div className={ `text-xs text-paidniceGreen-200 mb-2 px-2` }>{ `Quick Filters:` }</div>
                              { contactAgeRanges.map(range => (
                                <button
                                  key={ range.key }
                                  onClick={ () => navigateWithAgeFilter(`contacts`, range.key) }
                                  className={ `block px-2 py-1 text-xs text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600 rounded-sm w-full text-left` }
                                >
                                  { range.label }
                                </button>
                              )) }
                              <ContactQuickFiltersNav
                                buttonClassName={ `block px-2 py-1 text-xs text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600 rounded-sm w-full text-left` }
                                onNavigate={ () => setSidebarOpen(false) }
                              />
                            </div>
                            <SavedFiltersNav onNavigate={ () => setSidebarOpen(false) } />
                          </When>

                          { /* Quick Filters for Invoices - matches the aging buckets */ }
                          <When condition={ item.name === `Invoices` && item.current }>
                            <div className={ `ml-10 mt-2 space-y-1` }>
                              <div className={ `text-xs text-paidniceGreen-200 mb-2 px-2` }>{ `Quick Filters:` }</div>
                              { invoiceAgingBuckets.map(bucket => (
                                <button
                                  key={ bucket.key }
                                  type={ `button` }
                                  onClick={ () => navigateToInvoiceAgingBucket(bucket) }
                                  className={ `block px-2 py-1 text-xs text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600 rounded-sm w-full text-left` }
                                >
                                  { invoiceAgingBucketMenuLabel(bucket) }
                                </button>
                              )) }
                            </div>
                            <SavedFiltersNav type={ SavedFilterType.Invoice }
                              onNavigate={ () => setSidebarOpen(false) } />
                          </When>

                          { /* Quick Filters for Escalations */ }
                          <When condition={ item.name === `Escalations` && item.current }>
                            <div className={ `ml-10 mt-2 space-y-1` }>
                              <div className={ `text-xs text-paidniceGreen-200 mb-2 px-2` }>{ `Quick Filters:` }</div>
                              { escalationQuickFilters.map(filter => (
                                <button
                                  key={ filter.key }
                                  type={ `button` }
                                  onClick={ () => navigateToEscalationQuickFilter(filter.key) }
                                  className={ `block px-2 py-1 text-xs text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600 rounded-sm w-full text-left` }
                                >
                                  { filter.label }
                                </button>
                              )) }
                            </div>
                            <SavedFiltersNav type={ SavedFilterType.Escalation }
                              onNavigate={ () => setSidebarOpen(false) } />
                          </When>

                          { /* Quick Filters for Actions */ }
                          <When condition={ item.name === `Actions` && item.current }>
                            <div className={ `ml-10 mt-2 space-y-1` }>
                              <div className={ `text-xs text-paidniceGreen-200 mb-2 px-2` }>{ `Quick Filters:` }</div>
                              { actionQuickFilters.map(filter => (
                                <button
                                  key={ filter.key }
                                  type={ `button` }
                                  onClick={ () => navigateToActionQuickFilter(filter.key) }
                                  className={ `block px-2 py-1 text-xs text-paidniceGreen-100 hover:text-white hover:bg-paidniceGreen-600 rounded-sm w-full text-left` }
                                >
                                  { filter.label }
                                </button>
                              )) }
                            </div>
                            <SavedFiltersNav type={ SavedFilterType.Action }
                              onNavigate={ () => setSidebarOpen(false) } />
                          </When>
                        </Case>
                        <Default>
                          <Disclosure as={ `div` }
                            className={ `space-y-1 w-full` }
                            key={ item.name }>
                            { ({ open }) => (
                              <>
                                <DisclosureButton
                                  as={ `a` }
                                  className={ classNames(
                                    item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer`,
                                  ) }
                                >
                                  <item.icon
                                    className={ classNames(
                                      item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                      `mr-3 shrink-0 h-6 w-6`,
                                    ) }
                                    aria-hidden={ `true` }
                                  />
                                  <span className={ `flex-1` }>{ item.name }</span>

                                  <svg
                                    className={ classNames(
                                      open ? `text-gray-400 rotate-90` : `text-gray-300`,
                                      `ml-3 h-5 w-5 shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400`,
                                    ) }
                                    viewBox={ `0 0 20 20` }
                                    aria-hidden={ `true` }
                                  >
                                    <path d={ `M6 6L14 10L6 14V6Z` }
                                      fill={ `currentColor` } />
                                  </svg>
                                </DisclosureButton>
                                <DisclosurePanel className={ `space-y-1` }>
                                  { item.children.map(subItem => (
                                    <Switch key={ subItem.key || subItem.name }>
                                      <Case condition={ subItem.href === `ADD_NEW_GROUP` }>
                                        <DisclosureButton
                                          key={ subItem.name }
                                          as={ `div` }
                                        >
                                          <a
                                            href={ `#` }
                                            className={ classNames(
                                              subItem.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                                              `group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11`,
                                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                                            ) }
                                            onClick={ e => {
                                              e.preventDefault();
                                              onNewGroup();
                                            } }
                                          >
                                            <>
                                              <subItem.icon
                                                className={ classNames(
                                                  item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                                  `mr-3 shrink-0 h-6 w-6`,
                                                ) }
                                                aria-hidden={ `true` }
                                              />
                                              { subItem.name }
                                            </>
                                          </a>
                                        </DisclosureButton>
                                      </Case>
                                      <Default>

                                        <DisclosureButton
                                          key={ subItem.name }
                                          as={ `div` }
                                        >
                                          <WrappedLink
                                            to={ subItem.href }
                                            className={ classNames(
                                              subItem.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                                              `group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11`,
                                              notAllowed(item) && `opacity-50 cursor-not-allowed`,
                                            ) }
                                            disabled={ notAllowed(item) }
                                          >
                                            <>
                                              <subItem.icon
                                                className={ classNames(
                                                  item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                                  `mr-3 shrink-0 h-6 w-6`,
                                                ) }
                                                aria-hidden={ `true` }
                                              />
                                              { subItem.name }
                                              <When condition={ noSub(subItem) }>
                                                <Badge color={ `gray` }
                                                  message={ `No Plan` }
                                                  size={ `xs` } />
                                              </When>

                                              <When condition={ upgrade(subItem) }>
                                                <UpgradeBadge />
                                              </When>
                                            </>
                                          </WrappedLink>
                                        </DisclosureButton>
                                      </Default>
                                    </Switch>
                                  )) }
                                </DisclosurePanel>
                              </>
                            ) }
                          </Disclosure>
                        </Default>
                      </Switch>
                    );
                  }) }
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
          <div className={ `w-14 shrink-0` }
            aria-hidden={ `true` }>
            { /* Dummy element to force sidebar to shrink to fit close icon */ }
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
